/**
 * Helpers for turning Apps Script sheet layout JSON into renderable table cells.
 * Rows/cols in the layout are 1-indexed (Google Sheets).
 */

import {
  getPrimaryVariableToken,
  isMalformedVariableToken,
  isMixedVariableCell,
  parseCellVariableSegments
} from './variableTokenHelpers';
import { isCheckboxCell } from './sheetCheckboxHelpers';

/** Preview is 40% larger than the source sheet sizes (widths, heights, borders). */
export const SHEET_DISPLAY_SCALE = 1.4;

/** Font scale multiplier; 1 = no boost (0% increment). Layout geometry still uses SHEET_DISPLAY_SCALE. */
export const SHEET_FONT_DISPLAY_SCALE = 1.05;

const scalePx = value => Math.round((Number(value) || 0) * SHEET_DISPLAY_SCALE);

const toInt = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const isCellCoveredByMerge = (mergeMap, row1, col1) => {
  const info = mergeMap[`${row1},${col1}`];
  return Boolean(info && !info.isOrigin);
};

export const buildMergeMap = (merges = []) => {
  const mergeMap = {};

  (merges || []).forEach(merge => {
    const startRow = toInt(merge.startRow ?? merge.row ?? merge.r);
    const startCol = toInt(merge.startCol ?? merge.column ?? merge.col ?? merge.c);
    const endRow = toInt(merge.endRow ?? merge.end_row);
    const endCol = toInt(merge.endCol ?? merge.end_col);

    if (startRow == null || startCol == null || endRow == null || endCol == null) {
      return;
    }

    for (let r = startRow; r <= endRow; r++) {
      for (let c = startCol; c <= endCol; c++) {
        mergeMap[`${r},${c}`] = {
          isOrigin: r === startRow && c === startCol,
          rowSpan: endRow - startRow + 1,
          colSpan: endCol - startCol + 1,
          startRow,
          startCol,
          endRow,
          endCol
        };
      }
    }
  });

  return mergeMap;
};

const mapTextAlign = alignment => {
  if (!alignment) return 'left';
  const value = String(alignment).toLowerCase();
  if (value.includes('center') || value.includes('centre')) return 'center';
  if (value.includes('right')) return 'right';
  return 'left';
};

const mapVerticalAlign = alignment => {
  if (!alignment) return 'middle';
  const value = String(alignment).toLowerCase();
  if (value.includes('top')) return 'top';
  if (value.includes('bottom')) return 'bottom';
  return 'middle';
};

/**
 * Google Sheets wrap strategies:
 * OVERFLOW (default) — single line, paint into empty neighbors
 * WRAP — wrap at word boundaries inside the cell
 * CLIP — single line, clip at cell edge
 */
export const getWrapMode = wrapStrategy => {
  const value = String(wrapStrategy || '').toUpperCase();
  if (value.includes('WRAP')) return 'wrap';
  if (value.includes('CLIP')) return 'clip';
  return 'overflow'; // OVERFLOW and unknowns
};

const toCssColor = color => {
  if (!color) return '#000000';
  if (typeof color === 'string') return color;
  // Sheets API: { red, green, blue } in 0–1, or nested rgbColor / colorStyle
  const rgb = color.rgbColor || color.colorStyle?.rgbColor || color;
  if (rgb && (rgb.red != null || rgb.green != null || rgb.blue != null)) {
    const r = Math.round((rgb.red || 0) * 255);
    const g = Math.round((rgb.green || 0) * 255);
    const b = Math.round((rgb.blue || 0) * 255);
    return `#${[r, g, b]
      .map(n =>
        Math.max(0, Math.min(255, n))
          .toString(16)
          .padStart(2, '0')
      )
      .join('')}`;
  }
  return '#000000';
};

const borderCss = side => {
  if (!side) return undefined;
  const style = String(side.style || '').toUpperCase();
  if (!style || style.includes('NONE')) return undefined;

  const color = toCssColor(side.color);
  let widthPx = 1;
  let cssStyle = 'solid';

  if (side.width != null && Number(side.width) > 0) {
    widthPx = Number(side.width);
  } else if (style.includes('THICK')) {
    widthPx = 3;
  } else if (style.includes('MEDIUM') || style.includes('DOUBLE')) {
    widthPx = style.includes('DOUBLE') ? 3 : 2;
  }

  if (style.includes('DASHED')) cssStyle = 'dashed';
  if (style.includes('DOTTED')) cssStyle = 'dotted';
  if (style.includes('DOUBLE')) cssStyle = 'double';

  return `${scalePx(widthPx)}px ${cssStyle} ${color}`;
};

const pickBorderSide = (borders, row1, col1, side) => {
  const sides = borders?.[`${row1},${col1}`];
  return sides?.[side] || null;
};

/**
 * Borders for a cell or merge: walk the outer edges so merge outlines
 * still show when the API only marks perimeter cells.
 */
export const getRangeBorderStyle = (borders, startRow, startCol, endRow, endCol) => {
  if (!borders || borders.omitted) return {};

  let top;
  let bottom;
  let left;
  let right;

  for (let c = startCol; c <= endCol; c++) {
    top = top || pickBorderSide(borders, startRow, c, 'top');
    bottom = bottom || pickBorderSide(borders, endRow, c, 'bottom');
  }
  for (let r = startRow; r <= endRow; r++) {
    left = left || pickBorderSide(borders, r, startCol, 'left');
    right = right || pickBorderSide(borders, r, endCol, 'right');
  }

  // Also accept borders stored only on the origin (Sheets often does this for merges)
  const origin = borders[`${startRow},${startCol}`];
  if (origin) {
    top = top || origin.top;
    left = left || origin.left;
    bottom = bottom || origin.bottom;
    right = right || origin.right;
  }

  const style = {};
  const topCss = borderCss(top);
  const leftCss = borderCss(left);
  const bottomCss = borderCss(bottom);
  const rightCss = borderCss(right);
  if (topCss) style.borderTop = topCss;
  if (leftCss) style.borderLeft = leftCss;
  if (bottomCss) style.borderBottom = bottomCss;
  if (rightCss) style.borderRight = rightCss;
  return style;
};

export const getCellBorderStyle = (borders, row1, col1) =>
  getRangeBorderStyle(borders, row1, col1, row1, col1);

/** Soft fill so {{variable}} / input cells stand out from labels (full merge area). */
export const VARIABLE_CELL_BACKGROUND = '#E3F2FD';

export const buildVariableCellMeta = (row1, col1, cellText, hit = null) => {
  const rawValue = hit?.rawValue ?? cellText;
  const segments = parseCellVariableSegments(rawValue);
  const variables = segments.filter(s => s.type === 'variable');

  if (!variables.length) {
    if (!hit) return null;
    return {
      row: row1,
      col: col1,
      name: hit.name || '',
      fullToken: hit.fullToken || '',
      cellKind: hit.cellKind || 'variable',
      rawValue,
      segments: []
    };
  }

  const mixed = isMixedVariableCell({ segments, rawValue }, cellText);
  const primaryToken = getPrimaryVariableToken({ segments, fullToken: hit?.fullToken }, cellText);

  return {
    ...(hit || {}),
    row: row1,
    col: col1,
    name: variables[0].name,
    fullToken: mixed ? undefined : primaryToken,
    cellKind: mixed ? 'mixed' : 'variable',
    rawValue,
    segments
  };
};

export const getVariableCellMeta = (sheet, row0, col0) => {
  const row1 = row0 + 1;
  const col1 = col0 + 1;
  const cellText = getCellDisplayText(sheet, row0, col0);

  if (Array.isArray(sheet.variableCells)) {
    const hit = sheet.variableCells.find(cell => cell.row === row1 && cell.col === col1);
    if (hit) {
      if (hit.segments?.length) {
        const mixed = isMixedVariableCell(hit, cellText);
        return {
          ...hit,
          cellKind: mixed ? 'mixed' : hit.cellKind || 'variable',
          fullToken: mixed ? undefined : getPrimaryVariableToken(hit, cellText)
        };
      }

      const rawValue = hit.rawValue ?? cellText;
      const needsRepair =
        isMalformedVariableToken(hit.fullToken) ||
        (hit.cellKind === 'mixed' && !hit.segments?.length) ||
        parseCellVariableSegments(rawValue).filter(s => s.type === 'variable').length > 1;

      if (needsRepair || hit.cellKind === 'mixed') {
        return buildVariableCellMeta(row1, col1, cellText, { ...hit, rawValue });
      }

      return {
        ...hit,
        rawValue,
        segments: parseCellVariableSegments(rawValue)
      };
    }
  }

  const segments = parseCellVariableSegments(cellText);
  if (!segments.some(s => s.type === 'variable')) {
    const kind = sheet.cellKinds?.[row0]?.[col0];
    if (kind === 'variable' || kind === 'mixed') {
      return { row: row1, col: col1, name: '', fullToken: '', cellKind: kind, segments: [] };
    }
    return null;
  }

  return buildVariableCellMeta(row1, col1, cellText);
};

export const isVariableInputCell = (sheet, row0, col0) => Boolean(getVariableCellMeta(sheet, row0, col0));

export const getCellDisplayText = (sheet, row0, col0) => {
  const rawValue = sheet.values?.[row0]?.[col0];
  const display = sheet.displayValues?.[row0]?.[col0];

  if (isCheckboxCell(sheet, row0, col0)) {
    const checked = rawValue === true || display === 'TRUE' || display === '✓' || display === '☑';
    return checked ? '☑' : '☐';
  }

  if (display !== undefined && display !== null && display !== '') {
    return String(display);
  }

  const formula = sheet.formulas?.[row0]?.[col0];
  if (formula) return formula;

  if (rawValue === undefined || rawValue === null || rawValue === '') return '';
  return String(rawValue);
};

export const getCellStyle = (sheet, row0, col0, endRow1 = row0 + 1, endCol1 = col0 + 1) => {
  const row1 = row0 + 1;
  const col1 = col0 + 1;

  const background = sheet.backgrounds?.[row0]?.[col0];
  const fontColor = sheet.fontColors?.[row0]?.[col0];
  const fontFamily = sheet.fontFamilies?.[row0]?.[col0];
  const fontSize = sheet.fontSizes?.[row0]?.[col0];
  const fontWeight = sheet.fontWeights?.[row0]?.[col0];
  const fontStyle = sheet.fontStyles?.[row0]?.[col0];
  const hAlign = mapTextAlign(sheet.horizontalAlignments?.[row0]?.[col0]);
  const vAlign = mapVerticalAlign(sheet.verticalAlignments?.[row0]?.[col0]);
  const wrapMode = getWrapMode(sheet.wrapStrategies?.[row0]?.[col0]);

  const pt = (fontSize || 10) * SHEET_FONT_DISPLAY_SCALE;

  const hasFill =
    background &&
    String(background).trim() !== '' &&
    String(background).toLowerCase() !== '#ffffff' &&
    String(background).toLowerCase() !== 'white';

  const isVariable = isVariableInputCell(sheet, row0, col0);

  const borderStyle = getRangeBorderStyle(sheet.borders, row1, col1, endRow1, endCol1);
  const hasSheetBorder = Boolean(
    borderStyle.borderTop ||
      borderStyle.borderRight ||
      borderStyle.borderBottom ||
      borderStyle.borderLeft
  );

  const style = {
    ...borderStyle,
    // Variable/input cells: distinct fill over the whole cell (including merges).
    // Otherwise keep sheet fill, or transparent so OVERFLOW can show through empties.
    backgroundColor: isVariable
      ? VARIABLE_CELL_BACKGROUND
      : hasFill
        ? background
        : 'transparent',
    color: fontColor || '#000000',
    fontFamily: fontFamily || 'Arial, Helvetica, sans-serif',
    fontSize: `${pt}pt`,
    // Sheets-ish line box so wrapped labels don't collide
    lineHeight: `${Math.max(pt * 1.25, 14 * SHEET_FONT_DISPLAY_SCALE)}px`,
    fontWeight: String(fontWeight || '').toLowerCase() === 'bold' ? 700 : 400,
    fontStyle: String(fontStyle || '').toLowerCase() === 'italic' ? 'italic' : 'normal',
    textAlign: hAlign,
    alignItems: vAlign === 'top' ? 'flex-start' : vAlign === 'bottom' ? 'flex-end' : 'center',
    justifyContent: hAlign === 'center' ? 'center' : hAlign === 'right' ? 'flex-end' : 'flex-start'
  };

  if (wrapMode === 'wrap') {
    style.whiteSpace = 'pre-wrap';
    style.wordBreak = 'normal';
    style.overflowWrap = 'break-word';
    style.overflow = 'hidden';
  } else if (wrapMode === 'clip') {
    style.whiteSpace = 'nowrap';
    style.overflow = 'hidden';
    style.textOverflow = 'clip';
  } else {
    // OVERFLOW — spill into empty neighbors only (spillMaxWidth set in buildRenderableCells)
    style.whiteSpace = 'nowrap';
    style.overflow = 'visible';
  }

  return { style, wrapMode, hasSheetBorder, isVariable };
};

const cellHasContent = (sheet, row0, col0) => {
  const text = getCellDisplayText(sheet, row0, col0);
  return Boolean(text && String(text).trim());
};

/**
 * Google Sheets OVERFLOW: text extends right through empty cells, then clips
 * when the next cell has content (or is covered by another merge).
 *
 * Exception (Excel/Sheets): merged cells do NOT spill past the merge boundary —
 * overflow is clipped to the merged area even if the next cell is empty.
 */
const computeOverflowSpillMaxWidth = ({
  sheet,
  mergeMap,
  row0,
  endCol1,
  widthPx,
  visibleColSet,
  colWidths,
  isMerge
}) => {
  // Merged cells clip at the merge edge (matches Sheets templates with merges)
  if (isMerge) {
    return Math.max(widthPx - scalePx(6), 0);
  }

  const row1 = row0 + 1;
  let spill = widthPx;

  for (let c1 = endCol1 + 1; c1 <= sheet.cols; c1++) {
    const c0 = c1 - 1;
    if (!visibleColSet.has(c0)) continue;

    if (isCellCoveredByMerge(mergeMap, row1, c1)) break;

    const neighborMerge = mergeMap[`${row1},${c1}`];
    if (neighborMerge?.isOrigin) {
      if (cellHasContent(sheet, neighborMerge.startRow - 1, neighborMerge.startCol - 1)) break;
      let mergeW = 0;
      for (let mc = neighborMerge.startCol; mc <= neighborMerge.endCol; mc++) {
        if (visibleColSet.has(mc - 1)) mergeW += colWidths[mc - 1] || scalePx(100);
      }
      spill += mergeW;
      c1 = neighborMerge.endCol;
      continue;
    }

    if (cellHasContent(sheet, row0, c0)) break;
    spill += colWidths[c0] || scalePx(100);
  }

  // Leave room for scaled cell padding
  return Math.max(spill - scalePx(6), 0);
};

export const getVisibleRowIndexes = sheet => {
  const hidden = new Set((sheet.hiddenRows || []).map(toInt).filter(Boolean));
  const indexes = [];
  for (let r = 0; r < sheet.rows; r++) {
    if (!hidden.has(r + 1)) indexes.push(r);
  }
  return indexes;
};

export const getVisibleColIndexes = sheet => {
  const hidden = new Set((sheet.hiddenColumns || []).map(toInt).filter(Boolean));
  const indexes = [];
  for (let c = 0; c < sheet.cols; c++) {
    if (!hidden.has(c + 1)) indexes.push(c);
  }
  return indexes;
};

/**
 * Build only the cells that should be painted (merge origins + non-merged cells).
 * Uses CSS grid placement: 1-based lines matching Google sheet indexes after mapping
 * through visible row/col order.
 */
export const buildRenderableCells = sheet => {
  const mergeMap = buildMergeMap(sheet.merges || []);
  const visibleRows = getVisibleRowIndexes(sheet);
  const visibleCols = getVisibleColIndexes(sheet);
  const visibleColSet = new Set(visibleCols);
  const colWidths = (sheet.columnWidths || []).map(w => scalePx(w || 100));
  const rowHeights = (sheet.rowHeights || []).map(h => scalePx(h || 21));
  const defaultColW = scalePx(100);
  const defaultRowH = scalePx(21);

  const rowLineBySheetRow = {};
  visibleRows.forEach((row0, index) => {
    rowLineBySheetRow[row0 + 1] = index + 1;
  });

  const colLineBySheetCol = {};
  visibleCols.forEach((col0, index) => {
    colLineBySheetCol[col0 + 1] = index + 1;
  });

  const cells = [];
  const expandableRows = new Set();

  visibleRows.forEach(row0 => {
    visibleCols.forEach(col0 => {
      const row1 = row0 + 1;
      const col1 = col0 + 1;

      if (isCellCoveredByMerge(mergeMap, row1, col1)) {
        return;
      }

      const merge = mergeMap[`${row1},${col1}`];
      const endRow1 = merge?.isOrigin ? merge.endRow : row1;
      const endCol1 = merge?.isOrigin ? merge.endCol : col1;

      // Skip merges whose origin is visible but extends only over hidden areas
      const gridRowStart = rowLineBySheetRow[row1];
      const gridColStart = colLineBySheetCol[col1];
      if (!gridRowStart || !gridColStart) return;

      let gridRowEnd = gridRowStart + 1;
      let gridColEnd = gridColStart + 1;
      let widthPx = colWidths[col0] || defaultColW;
      let heightPx = rowHeights[row0] || defaultRowH;

      if (merge?.isOrigin) {
        // End line = first visible line AFTER the last visible spanned sheet row/col
        let lastVisibleRowLine = gridRowStart;
        for (let r = row1; r <= endRow1; r++) {
          if (rowLineBySheetRow[r]) lastVisibleRowLine = rowLineBySheetRow[r];
        }
        gridRowEnd = lastVisibleRowLine + 1;

        let lastVisibleColLine = gridColStart;
        widthPx = 0;
        for (let c = col1; c <= endCol1; c++) {
          if (colLineBySheetCol[c]) {
            lastVisibleColLine = colLineBySheetCol[c];
            widthPx += colWidths[c - 1] || defaultColW;
          }
        }
        gridColEnd = lastVisibleColLine + 1;

        heightPx = 0;
        for (let r = row1; r <= endRow1; r++) {
          if (rowLineBySheetRow[r]) {
            heightPx += rowHeights[r - 1] || defaultRowH;
          }
        }
      }

      const text = getCellDisplayText(sheet, row0, col0);
      const { style, wrapMode, hasSheetBorder, isVariable } = getCellStyle(
        sheet,
        row0,
        col0,
        endRow1,
        endCol1
      );
      const hasText = Boolean(text && String(text).trim());
      const isCheckbox = isCheckboxCell(sheet, row0, col0);

      let spillMaxWidth = null;
      if (wrapMode === 'overflow' && hasText && !isCheckbox) {
        spillMaxWidth = computeOverflowSpillMaxWidth({
          sheet,
          mergeMap,
          row0,
          endCol1,
          widthPx,
          visibleColSet,
          colWidths,
          isMerge: Boolean(merge?.isOrigin)
        });
      }

      const variableMeta = isVariable ? getVariableCellMeta(sheet, row0, col0) : null;
      if (variableMeta && isMixedVariableCell(variableMeta, text)) {
        for (let r = row1; r <= endRow1; r++) {
          const visibleRow0 = r - 1;
          if (visibleRows.includes(visibleRow0)) {
            expandableRows.add(visibleRow0);
          }
        }
      }

      cells.push({
        key: `${row0}-${col0}`,
        row0,
        col0,
        text,
        formula: sheet.formulas?.[row0]?.[col0] || '',
        style,
        wrapMode,
        hasText,
        hasSheetBorder,
        isVariable,
        isCheckbox,
        variableMeta,
        spillMaxWidth,
        gridRow: `${gridRowStart} / ${gridRowEnd}`,
        gridColumn: `${gridColStart} / ${gridColEnd}`,
        widthPx,
        heightPx
      });
    });
  });

  // Paint order: empty → text → overflow → sheet borders last so thick
  // section boxes aren't covered by neighboring cells.
  cells.sort((a, b) => {
    const rank = cell =>
      (cell.hasSheetBorder ? 4 : 0) +
      (cell.wrapMode === 'overflow' && cell.hasText ? 2 : cell.hasText ? 1 : 0);
    return rank(a) - rank(b);
  });

  const gridTemplateRowTracks = visibleRows.map(row0 => {
    const h = rowHeights[row0] || defaultRowH;
    return expandableRows.has(row0) ? `minmax(${h}px, auto)` : `${h}px`;
  });

  return {
    cells,
    visibleRows,
    visibleCols,
    mergeCount: Object.keys(mergeMap).filter(key => mergeMap[key].isOrigin).length,
    gridTemplateColumns: visibleCols.map(col0 => `${colWidths[col0] || defaultColW}px`).join(' '),
    gridTemplateRowTracks,
    gridTemplateRows: gridTemplateRowTracks.join(' '),
    tableWidth: visibleCols.reduce((sum, col0) => sum + (colWidths[col0] || defaultColW), 0),
    scale: SHEET_DISPLAY_SCALE
  };
};
